// ホーム画面に追加（PWA）。Android・PC の Chrome / Edge はボタン1つでインストールの確認が出る（beforeinstallprompt）。
// iPhone・iPad はブラウザから追加させる仕組みが無いので、共有ボタン →「ホーム画面に追加」の手順を絵つきで出す。
import { icon } from './icons';
import { openModal, toast } from './overlay';

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((f) => f());
/** インストールできる状態が変わったら（ボタンの出し分けを描き直す） */
export function onInstallChange(f: () => void) {
  listeners.add(f);
  return () => listeners.delete(f);
}

window.addEventListener('beforeinstallprompt', (e) => {
  // ブラウザの自動の小さな案内は出さず、こちらのボタンから出す
  e.preventDefault();
  deferred = e as InstallPrompt;
  changed();
});
window.addEventListener('appinstalled', () => {
  deferred = null;
  changed();
  toast('ホーム画面に追加しました', 'good');
});

/** ホーム画面のアイコンから開いている */
export const isStandalone = () =>
  matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches || (navigator as { standalone?: boolean }).standalone === true;
/** iPhone・iPad（iPadOS は Mac として名乗るのでタッチで見分ける） */
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isMobile = () => isIOS() || /Android/.test(navigator.userAgent);

/** ホーム画面に追加をすすめるか（アプリとして開いていない・スマホか、ボタン1つで入れられるブラウザ） */
export const canSuggestInstall = () => !isStandalone() && (isMobile() || !!deferred);

/** ボタン1つで追加（できないブラウザは手順を出す） */
export async function installApp() {
  if (deferred) {
    const d = deferred;
    deferred = null;
    await d.prompt();
    await d.userChoice.catch(() => null);
    changed();
    return;
  }
  const ios = isIOS();
  const steps = ios
    ? [
        ['1', `画面の下（iPad は上）の<b>共有ボタン</b> <span class="pwa-share" aria-hidden="true"></span> を押す`],
        ['2', '一覧を下へ送り<b>「ホーム画面に追加」</b>を押す'],
        ['3', '右上の<b>「追加」</b>を押す'],
      ]
    : [
        ['1', 'ブラウザの<b>メニュー（⋮）</b>を押す'],
        ['2', '<b>「ホーム画面に追加」</b>か<b>「アプリをインストール」</b>を押す'],
      ];
  openModal(
    `<div class="pwa-how">
      <img class="pwa-icon" src="/icons/icon-192.png" alt="" width="72" height="72">
      <p>ホーム画面のアイコンから、アプリのように全画面で遊べます。</p>
      <ol>${steps.map(([n, t]) => `<li><b class="pwa-no">${n}</b><span>${t}</span></li>`).join('')}</ol>
      ${ios ? '<p class="muted small">Safari 以外（Chrome など）でも、共有ボタンから同じように追加できます。</p>' : ''}
      <button class="btn primary wide" data-close>${icon('check')}わかった</button>
    </div>`,
    { title: 'ホーム画面に追加' },
  );
}

/** オフラインのときに最後の画面の枠だけ出せるように（キャッシュは public/sw.js が持つ）。プッシュ通知の表示も sw.js */
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));
  // 通知を押した：開いていた画面をその通知の画面へ（sw.js の notificationclick から）
  navigator.serviceWorker.addEventListener('message', (e) => {
    const d = e.data as { type?: string; url?: string } | null;
    if (d?.type === 'open' && typeof d.url === 'string' && d.url.startsWith('#/')) location.hash = d.url;
  });
}
